import { useId, useState } from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { CheckCircle2, MessageSquareWarning, Loader2 } from "lucide-react";

import { useReviewWeeklyReport } from "@/hooks/useWeeklyReports";
import type { WeeklyReport, WeeklyReportReviewStatus } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FieldLabel } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { weeklyReportMessages as msg } from "./weekly-report-messages";

/**
 * T-28: PMO review of a submitted weekly report.
 *
 * Everyone who can see a submitted report sees its review state (badge + reviewer note).
 * Leadership/admin additionally get Approve / Request changes. The server is the enforcer
 * (POST /api/projects/{projectId}/weekly-reports/{reportId} is leadership/admin only); this UI
 * hides the actions for other roles.
 */

const NOTE_MAX = 2000;

const REVIEW_BADGE: Record<
  WeeklyReportReviewStatus,
  { variant: "info" | "success" | "warning" }
> = {
  pending: { variant: "info" },
  approved: { variant: "success" },
  changes_requested: { variant: "warning" },
};

/** True for roles that may review (mirrors the backend withRoles list). */
export function canReviewReports(role: string | undefined): boolean {
  return role === "leadership" || role === "admin";
}

export function ReviewStatusBadge({
  status,
}: {
  status: WeeklyReportReviewStatus | null | undefined;
}): JSX.Element | null {
  if (!status) return null;
  return (
    <Badge
      variant={REVIEW_BADGE[status].variant}
      data-testid="weekly-report-review-badge"
    >
      {msg.review.statusLabel[status]}
    </Badge>
  );
}

function reviewErrorMessage(err: unknown): string {
  if (isAxiosError<{ code?: string; message?: string }>(err)) {
    const code = err.response?.data?.code;
    if (code === "REPORT_STALE") return msg.review.stale;
    if (code === "REPORT_NOT_SUBMITTED") return msg.review.notSubmitted;
    return err.response?.data?.message ?? msg.review.failure;
  }
  return msg.review.failure;
}

export function WeeklyReportReview({
  projectId,
  report,
  canReview,
}: {
  projectId: string;
  report: WeeklyReport;
  canReview: boolean;
}): JSX.Element | null {
  const review = useReviewWeeklyReport(projectId);
  const [requesting, setRequesting] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  const errorId = useId();

  // Drafts are never reviewable; pre-V052 rows have no review_status.
  if (report.status === "draft" || !report.review_status) return null;

  const decide = async (
    decision: "approved" | "changes_requested",
  ): Promise<void> => {
    const trimmed = note.trim();
    if (decision === "changes_requested" && !trimmed) {
      setError(msg.review.noteRequired);
      return;
    }
    setError(null);
    try {
      await review.mutateAsync({
        reportId: report.id,
        input: {
          decision,
          ...(decision === "changes_requested" ? { note: trimmed } : {}),
          ...(report.updated_at
            ? { expected_updated_at: report.updated_at }
            : {}),
        },
      });
      toast.success(
        decision === "approved"
          ? msg.review.approveSuccess
          : msg.review.changesSuccess,
      );
      setRequesting(false);
      setNote("");
    } catch (err) {
      setError(reviewErrorMessage(err));
    }
  };

  const busy = review.isPending;

  return (
    <div
      className="mb-3 flex flex-col gap-3 rounded-md border border-outlines bg-subtle-bg/30 p-3"
      data-testid="weekly-report-review"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <ReviewStatusBadge status={report.review_status} />
          {report.reviewed_by_email && report.reviewed_at && (
            <span className="text-xs text-text-secondary">
              {msg.review.reviewedBy(
                report.reviewed_by_email,
                new Date(report.reviewed_at).toLocaleString(),
              )}
            </span>
          )}
        </div>
        {canReview && !requesting && (
          <div className="flex flex-wrap gap-2">
            {report.review_status !== "approved" && (
              <Button
                size="sm"
                variant="success"
                disabled={busy}
                onClick={() => void decide("approved")}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                )}
                {msg.review.approve}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setError(null);
                setRequesting(true);
              }}
            >
              <MessageSquareWarning className="h-4 w-4" aria-hidden="true" />
              {msg.review.requestChanges}
            </Button>
          </div>
        )}
      </div>

      {report.review_note && !requesting && (
        <p className="whitespace-pre-wrap text-sm text-text-primary">
          {report.review_note}
        </p>
      )}

      {canReview && requesting && (
        <div className="flex flex-col gap-2">
          <FieldLabel htmlFor={noteId}>
            {msg.review.noteLabel} <span aria-hidden="true">*</span>
          </FieldLabel>
          <Textarea
            id={noteId}
            value={note}
            maxLength={NOTE_MAX}
            rows={3}
            aria-required="true"
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            placeholder={msg.review.notePlaceholder}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setRequesting(false);
                setNote("");
                setError(null);
              }}
            >
              {msg.review.cancel}
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void decide("changes_requested")}
            >
              {busy && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {msg.review.submitChanges}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
}

export default WeeklyReportReview;
